import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/auth/authContext';
import { getUnreadCount } from '@/services/chatService';
import { subscribeChatSocket } from '@/services/chatSocket';
import { onBadgesChanged } from '@/services/badgeSignal';

/** Inbox/badge refresh interval. Also the socket-down worst case for freshness. */
export const CHAT_POLL_MS = 20_000;

/**
 * The tab-bar badge (replaces the hardcoded chatBadge={1}).
 *
 * Polls AND listens. The poll is not a socket-failure branch — it always runs,
 * so the path that keeps chat correct without a socket is the path that is
 * exercised on every session.
 */
export function useUnreadCount(): number {
  const { status } = useAuth();
  const [total, setTotal] = useState(0);

  const refresh = useCallback(() => {
    if (status !== 'authed') {
      setTotal(0);
      return;
    }
    getUnreadCount()
      .then(setTotal)
      .catch(() => {
        /* a badge is not worth surfacing an error for */
      });
  }, [status]);

  useEffect(() => {
    refresh();
    if (status !== 'authed') return;

    const timer = window.setInterval(refresh, CHAT_POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);

    // Any inbox change (new message, or the peer reading ours) can move the count.
    const unsubscribe = subscribeChatSocket({ onConversationUpdated: refresh });
    // Reading a thread, here or on another device.
    const offBadges = onBadgesChanged(refresh);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
      offBadges();
    };
  }, [status, refresh]);

  return total;
}
